/**
 * Lite 訂閱 API
 * TapPay Pay by Prime 付款
 */

import { getApiBase } from './apiUtils.js';
import { apiFetch } from './apiUtils.js';
import { handleLiteErrorResponse } from './liteApiError.js';

export interface PayCardholder {
  name: string;
  phone_number: string;
  email: string;
}

export interface PayLiteSubscriptionResult {
  success: true;
  periodEnd: string;
}

export interface SubscriptionStatus {
  planSlug: 'free' | 'trial' | 'basic' | 'plus' | 'pro';
  used: number;
  limit: number;
  periodEnd: string | null;
  cancelAtPeriodEnd?: boolean;
  isTrial: boolean;
  trialEndsAt: string | null;
}

/** 訂閱 API 可選情境：Group 傳 groupId，Square 傳 squareSuffix（與 Group 共用同一訂閱） */
export interface LiteSubscriptionContext {
  groupId?: string | null;
  squareSuffix?: string | null;
}

/**
 * 取得訂閱狀態：目前方案、本月已使用／上限、預計更新日
 * Group 傳 groupId；Square 傳 squareSuffix，後端依該商家的使用者解析群組訂閱
 * @param lineIdToken 選填；開發環境 JWT 過期時，可傳 mock_token_ 讓後端以 X-Line-ID-Token 取得 userId
 */
export async function getSubscriptionStatus(
  jwt: string,
  lineIdToken?: string,
  context?: LiteSubscriptionContext
): Promise<SubscriptionStatus> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');
  const headers: Record<string, string> = { Authorization: `Bearer ${jwt}` };
  if (lineIdToken) headers['X-Line-ID-Token'] = lineIdToken;

  let url: string;
  let res: Response;
  if (context?.squareSuffix?.trim()) {
    url = `${apiBase}/api/lite/subscription/status`;
    res = await liteApiFetch(
      url,
      { method: 'GET', headers },
      { squareSuffix: context.squareSuffix.trim() }
    );
  } else if (context?.groupId?.trim()) {
    url = `${apiBase}/api/lite/subscription/status?groupId=${encodeURIComponent(context.groupId)}`;
    res = await apiFetch(url, { method: 'GET', headers });
  } else {
    throw new Error('請提供 groupId 或 squareSuffix');
  }

  if (!res.ok) {
    await handleLiteErrorResponse(res, {
      defaultMessage: '取得訂閱狀態失敗，請稍後再試',
    });
  }
  return res.json() as Promise<SubscriptionStatus>;
}

/**
 * 訂閱付款（Pay by Prime）
 * Group 傳 context.groupId；Square 傳 context.squareSuffix
 */
export async function payLiteSubscription(
  prime: string,
  planSlug: 'basic' | 'plus' | 'pro',
  cardholder: PayCardholder,
  jwt: string,
  lineIdToken?: string,
  context?: LiteSubscriptionContext
): Promise<PayLiteSubscriptionResult> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
  };
  if (lineIdToken) headers['X-Line-ID-Token'] = lineIdToken;

  let url: string;
  let res: Response;
  if (context?.squareSuffix?.trim()) {
    url = `${apiBase}/api/lite/subscription/pay`;
    res = await liteApiFetch(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ prime, planSlug, cardholder }),
      },
      { squareSuffix: context.squareSuffix.trim() }
    );
  } else if (context?.groupId?.trim()) {
    url = `${apiBase}/api/lite/subscription/pay?groupId=${encodeURIComponent(context.groupId)}`;
    res = await apiFetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ prime, planSlug, cardholder }),
    });
  } else {
    throw new Error('請提供 groupId 或 squareSuffix');
  }

  if (!res.ok) {
    await handleLiteErrorResponse(res, {
      defaultMessage: '付款失敗，請稍後再試',
    });
  }

  return res.json() as Promise<PayLiteSubscriptionResult>;
}

export interface PayLiteSubscriptionEcPayResult {
  paymentUrl: string;
  formData: Record<string, string>;
}

/** 發票參數（綠界 pay-ecpay 選填；若提供則需 invoiceEmail） */
export interface PayLiteEcPayInvoice {
  invoiceType: 'personal' | 'company' | 'donation';
  taxId?: string;
  invoiceTitle?: string;
  carrierType?: 'member' | 'cellphone' | 'natural';
  carrierNum?: string;
  loveCode?: string;
  invoiceEmail: string;
}

/**
 * 訂閱付款（綠界金流）
 * Group 傳 context.groupId；Square 傳 context.squareSuffix
 */
export async function payLiteSubscriptionEcPay(
  planSlug: 'basic' | 'plus' | 'pro',
  jwt: string,
  lineIdToken?: string,
  invoice?: PayLiteEcPayInvoice,
  context?: LiteSubscriptionContext
): Promise<PayLiteSubscriptionEcPayResult> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
  };
  if (lineIdToken) headers['X-Line-ID-Token'] = lineIdToken;

  let url: string;
  let res: Response;
  if (context?.squareSuffix?.trim()) {
    url = `${apiBase}/api/lite/subscription/pay-ecpay`;
    res = await liteApiFetch(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ planSlug, invoice: invoice ?? undefined }),
      },
      { squareSuffix: context.squareSuffix.trim() }
    );
  } else if (context?.groupId?.trim()) {
    url = `${apiBase}/api/lite/subscription/pay-ecpay?groupId=${encodeURIComponent(context.groupId)}`;
    res = await apiFetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ planSlug, invoice: invoice ?? undefined }),
    });
  } else {
    throw new Error('請提供 groupId 或 squareSuffix');
  }

  if (!res.ok) {
    await handleLiteErrorResponse(res, {
      defaultMessage: '取得綠界付款表單失敗，請稍後再試',
    });
  }

  return res.json() as Promise<PayLiteSubscriptionEcPayResult>;
}

/** 取消訂閱回饋（可選） */
export interface CancelSubscriptionFeedback {
  reasons?: string[];
  missingFeature?: string | null;
  otherReason?: string | null;
}

/**
 * 取消訂閱：設定本期結束後不續訂
 * Group 傳 context.groupId；Square 傳 context.squareSuffix
 */
export async function cancelSubscription(
  jwt: string,
  feedback?: CancelSubscriptionFeedback,
  lineIdToken?: string,
  context?: LiteSubscriptionContext
): Promise<{ success: true }> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');
  const headers: Record<string, string> = { Authorization: `Bearer ${jwt}` };
  if (feedback != null) headers['Content-Type'] = 'application/json';
  if (lineIdToken) headers['X-Line-ID-Token'] = lineIdToken;

  let url: string;
  let res: Response;
  if (context?.squareSuffix?.trim()) {
    url = `${apiBase}/api/lite/subscription/cancel`;
    res = await liteApiFetch(
      url,
      {
        method: 'POST',
        headers,
        body: feedback != null ? JSON.stringify(feedback) : undefined,
      },
      { squareSuffix: context.squareSuffix.trim() }
    );
  } else if (context?.groupId?.trim()) {
    url = `${apiBase}/api/lite/subscription/cancel?groupId=${encodeURIComponent(context.groupId)}`;
    res = await apiFetch(url, {
      method: 'POST',
      headers,
      body: feedback != null ? JSON.stringify(feedback) : undefined,
    });
  } else {
    throw new Error('請提供 groupId 或 squareSuffix');
  }
  if (!res.ok) {
    await handleLiteErrorResponse(res, {
      defaultMessage: '取消訂閱失敗，請稍後再試',
    });
  }
  return res.json() as Promise<{ success: true }>;
}
